import React, { useState, useEffect } from "react";
import "./Profile.css";
import { API_URL } from "../config";

interface SocialMedia {
  id: number;
  platform_name: string;
  profile_url: string;
}

interface UserProfile {
  id: number;
  company_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  occupation: string | null;
  about_self: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  address_line_3: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code: string | null;
  social_media: SocialMedia[];
  website: string | null;
  projects_done: number;
  projects_deleted: number;
  projects_shared: number;
  projects_got_shared: number;
  display_picture: string | null;
  date_joined: string;
  title?: string | null;
  gstin?: string | null;
  current_plan?: string | null;
}

interface CompanyProfile {
  company_id: number;
  company_name: string;
  created_date: string;
  address_line_1: string | null;
  address_line_2: string | null;
  address_line_3: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code: string | null;
  company_website: string | null;
  gst_number: string | null;
  company_type: string | null;
  number_of_people: number | null;
  admin_status: string;
  admin_email: string;
  social_media: SocialMedia[];
}

const Profile: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"personal" | "company">("personal");
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);
  const [userFormData, setUserFormData] = useState<any>({});
  const [companyFormData, setCompanyFormData] = useState<any>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark";
  });

  useEffect(() => {
    const checkTheme = () => {
      const savedTheme = localStorage.getItem("theme");
      const newIsDarkMode = savedTheme === "dark";
      setIsDarkMode(newIsDarkMode);
      document.body.classList.toggle("dark", newIsDarkMode);
    };

    checkTheme();
    window.addEventListener("storage", checkTheme);
    const interval = setInterval(checkTheme, 100);

    return () => {
      window.removeEventListener("storage", checkTheme);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);

  const backendURL = `${API_URL}/auth`;

  const shouldShowCompanySection = (): boolean => {
    const userCompanyName = userProfile?.company_name?.trim();
    const companyName = companyProfile?.company_name?.trim();

    return !!(
      userCompanyName &&
      userCompanyName !== "" &&
      companyProfile &&
      companyName &&
      companyName !== ""
    );
  };

  useEffect(() => {
    const cachedProfile = localStorage.getItem("userProfile");
    const cachedCompany = localStorage.getItem("companyProfile");

    if (cachedProfile) {
      const profileData = JSON.parse(cachedProfile);
      const companyData = cachedCompany ? JSON.parse(cachedCompany) : null;

      setUserProfile(profileData);
      setCompanyProfile(companyData);

      const userFormWithSocial = {
        ...profileData,
        social_media_data:
          profileData.social_media?.map((sm: any) => ({
            ...sm,
            isExisting: true,
          })) || [],
      };

      const companyFormWithSocial = companyData
        ? {
          ...companyData,
          social_media_data:
            companyData.social_media?.map((sm: any) => ({
              ...sm,
              isExisting: true,
            })) || [],
        }
        : {};

      setUserFormData(userFormWithSocial);
      setCompanyFormData(companyFormWithSocial);
      setImagePreview(profileData.display_picture);
    } else {
      fetchProfile();
    }
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${backendURL}/profile/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.profile);
        setCompanyProfile(data.company);

        const userFormWithSocial = {
          ...data.profile,
          social_media_data:
            data.profile.social_media?.map((sm: any) => ({
              ...sm,
              isExisting: true,
            })) || [],
        };

        const companyFormWithSocial = data.company
          ? {
            ...data.company,
            social_media_data:
              data.company.social_media?.map((sm: any) => ({
                ...sm,
                isExisting: true,
              })) || [],
          }
          : {};

        setUserFormData(userFormWithSocial);
        setCompanyFormData(companyFormWithSocial);
        setImagePreview(data.profile.display_picture);

        if (
          !data.profile.company_name?.trim() ||
          !data.company?.company_name?.trim()
        ) {
          setActiveTab("personal");
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target?.result as string;
        setImagePreview(base64String);
        setUserFormData({ ...userFormData, display_picture: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUserInputChange = (field: string, value: string) => {
    setUserFormData({ ...userFormData, [field]: value });
  };

  const handleCompanyInputChange = (field: string, value: string) => {
    setCompanyFormData({ ...companyFormData, [field]: value });
  };

  const handleSocialMediaChange = (
    index: number,
    field: string,
    value: string,
    isUser: boolean
  ) => {
    const formData = isUser ? userFormData : companyFormData;
    const setFormData = isUser ? setUserFormData : setCompanyFormData;

    const socialMedia = [...(formData.social_media_data || [])];
    if (!socialMedia[index]) {
      socialMedia[index] = {
        platform_name: "",
        profile_url: "",
        isExisting: false,
      };
    }
    socialMedia[index][field] = value;

    setFormData({ ...formData, social_media_data: socialMedia });
  };

  const addSocialMedia = (isUser: boolean) => {
    const formData = isUser ? userFormData : companyFormData;
    const setFormData = isUser ? setUserFormData : setCompanyFormData;

    const socialMedia = [...(formData.social_media_data || [])];
    socialMedia.push({ platform_name: "", profile_url: "", isExisting: false });

    setFormData({ ...formData, social_media_data: socialMedia });
  };

  const removeSocialMedia = (index: number, isUser: boolean) => {
    const formData = isUser ? userFormData : companyFormData;
    const setFormData = isUser ? setUserFormData : setCompanyFormData;

    const socialMedia = [...(formData.social_media_data || [])];
    socialMedia.splice(index, 1);

    setFormData({ ...formData, social_media_data: socialMedia });
  };

  const updateUserProfile = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem("access_token");

      const cleanedSocialMedia =
        userFormData.social_media_data
          ?.filter((sm: any) => sm.platform_name && sm.profile_url)
          ?.map((sm: any) => ({
            platform_name: sm.platform_name,
            profile_url: sm.profile_url,
          })) || [];

      const cleanedFormData = {
        ...userFormData,
        social_media_data: cleanedSocialMedia,
      };

      const response = await fetch(`${backendURL}/profile/update/`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cleanedFormData),
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.profile);

        const updatedFormData = {
          ...data.profile,
          social_media_data:
            data.profile.social_media?.map((sm: any) => ({
              ...sm,
              isExisting: true,
            })) || [],
        };
        setUserFormData(updatedFormData);

        localStorage.setItem("userProfile", JSON.stringify(data.profile));

        window.location.reload();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Error updating profile");
    } finally {
      setUpdating(false);
    }
  };

  const updateCompanyProfile = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem("access_token");

      const cleanedSocialMedia =
        companyFormData.social_media_data
          ?.filter((sm: any) => sm.platform_name && sm.profile_url)
          ?.map((sm: any) => ({
            platform_name: sm.platform_name,
            profile_url: sm.profile_url,
          })) || [];

      const cleanedFormData = {
        ...companyFormData,
        social_media_data: cleanedSocialMedia,
      };

      const response = await fetch(`${backendURL}/company/update/`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cleanedFormData),
      });

      if (response.ok) {
        const data = await response.json();
        setCompanyProfile(data.company);

        const updatedFormData = {
          ...data.company,
          social_media_data:
            data.company.social_media?.map((sm: any) => ({
              ...sm,
              isExisting: true,
            })) || [],
        };
        setCompanyFormData(updatedFormData);

        localStorage.setItem("companyProfile", JSON.stringify(data.company));

        window.location.reload();
      }
    } catch (error) {
      console.error("Error updating company profile:", error);
      alert("Error updating company profile");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original profile data
    if (activeTab === "personal" && userProfile) {
      const userFormWithSocial = {
        ...userProfile,
        social_media_data:
          userProfile.social_media?.map((sm: any) => ({
            ...sm,
            isExisting: true,
          })) || [],
      };
      setUserFormData(userFormWithSocial);
      setImagePreview(userProfile.display_picture);
    } else if (activeTab === "company" && companyProfile) {
      const companyFormWithSocial = {
        ...companyProfile,
        social_media_data:
          companyProfile.social_media?.map((sm: any) => ({
            ...sm,
            isExisting: true,
          })) || [],
      };
      setCompanyFormData(companyFormWithSocial);
    }
    setIsEditing(false);
  };

  const getProfileCompletion = (profile: any): number => {
    if (!profile) return 0;
    const fieldsToCheck = [
      'first_name', 'last_name', 'email', 'phone_number', 
      'company_name', 'occupation', 'about_self', 'address_line_1', 
      'city', 'state', 'country', 'postal_code'
    ];
    const filledFields = fieldsToCheck.filter(
      (field) => profile[field] !== null && profile[field] !== "" && profile[field] !== undefined
    );
    return Math.round((filledFields.length / fieldsToCheck.length) * 100);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  // SVG Icons
  const EditIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const MailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const PhoneIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const PlusIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );

  const XIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );

  const BackArrow = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const BuildingIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 21H21M5 21V7L12 3L19 7V21M9 9H10M14 9H15M9 13H10M14 13H15M9 17H10M14 17H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className={`profile-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Back Button */}
      {/* <div className="profile-back-btn" onClick={() => window.history.back()}>
        <BackArrow />
        <span>Back</span>
      </div> */}

      {/* Page Title */}
      <h1 className="profile-page-title">Your Profile</h1>

      {/* Tab Navigation */}
      {/* <div className="profile-tab-nav">
        <button
          className={`profile-tab ${activeTab === "personal" ? "active" : ""}`}
          onClick={() => setActiveTab("personal")}
        >
          Personal Profile
        </button>
        {shouldShowCompanySection() && (
          <button
            className={`profile-tab ${activeTab === "company" ? "active" : ""}`}
            onClick={() => setActiveTab("company")}
          >
            Company Profile
          </button>
        )}
      </div> */}

      {/* Personal Profile */}
      {activeTab === "personal" && (
        <div className="profile-content-wrapper">
          {/* Left Sidebar - User Card */}
          <div className="profile-sidebar-card">
            <div className="profile-avatar-section">
              <div className="profile-avatar-wrapper">
                {imagePreview ? (
                  <img src={imagePreview} alt="Profile" className="profile-avatar-img" />
                ) : (
                  <div className="profile-avatar-placeholder">
                    {userProfile?.first_name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                )}
                {isEditing && (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="profile-avatar-input"
                      id="profile-avatar-upload"
                    />
                    <label htmlFor="profile-avatar-upload" className="profile-avatar-edit">
                      <EditIcon />
                    </label>
                  </>
                )}
              </div>
            </div>

            <div className="profile-user-info">
              <h2 className="profile-user-name">
                {userProfile?.first_name} {userProfile?.last_name}
              </h2>
              <p className="profile-user-company">{userProfile?.company_name}</p>
              
              <div className="profile-contact-info">
                <div className="profile-contact-item">
                  <MailIcon />
                  <span>{userProfile?.email}</span>
                </div>
                <div className="profile-contact-item">
                  <PhoneIcon />
                  <span>{userProfile?.phone_number}</span>
                </div>
              </div>

              {/* Profile Completion */}
              <div className="profile-completion-section">
                <div className="profile-completion-circle">
                  <svg className="profile-completion-svg" viewBox="0 0 100 100">
                    <defs>
                      <linearGradient id="completionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#34d399" />
                      </linearGradient>
                    </defs>
                    <circle
                      className="profile-completion-bg"
                      cx="50"
                      cy="50"
                      r="42"
                    />
                    <circle
                      className="profile-completion-fill"
                      cx="50"
                      cy="50"
                      r="42"
                      stroke="url(#completionGradient)"
                      style={{
                        strokeDasharray: `${getProfileCompletion(userProfile) * 2.64} 264`,
                      }}
                    />
                  </svg>
                  <div className="profile-completion-text">
                    <span className="profile-completion-number">{getProfileCompletion(userProfile)}%</span>
                  </div>
                </div>
                <p className="profile-completion-label">Profile Status</p>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="profile-form-card">
            <div className="profile-form-header">
              <h3 className="profile-section-title">Personal Information</h3>
              {!isEditing && (
                <button className="profile-edit-btn" onClick={handleEditClick}>
                  <EditIcon />
                  Edit
                </button>
              )}
            </div>

            {/* Row 1: First Name, Last Name */}
            <div className="profile-form-row two-col">
              <div className="profile-input-group">
                <label className="profile-label">First Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.first_name || ""}
                    onChange={(e) => handleUserInputChange("first_name", e.target.value)}
                    placeholder="Enter first name"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.first_name || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group">
                <label className="profile-label">Last Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.last_name || ""}
                    onChange={(e) => handleUserInputChange("last_name", e.target.value)}
                    placeholder="Enter last name"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.last_name || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: Company Name, Occupation */}
            <div className="profile-form-row two-col">
              <div className="profile-input-group">
                <label className="profile-label">Company Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.company_name || ""}
                    onChange={(e) => handleUserInputChange("company_name", e.target.value)}
                    placeholder="Enter company name"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.company_name || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group">
                <label className="profile-label">Occupation</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.occupation || ""}
                    onChange={(e) => handleUserInputChange("occupation", e.target.value)}
                    placeholder="Enter occupation"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.occupation || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Row 3: Current Plan */}
            {/* <div className="profile-form-row single-col">
              <div className="profile-input-group">
                <label className="profile-label">Current Plan</label>
                <div className="profile-plan-input">
                  <input
                    type="text"
                    className="profile-input readonly"
                    value={userFormData.current_plan || "Premium Plan, Rs2999"}
                    readOnly
                  />
                  <button className="profile-upgrade-btn">Upgrade Now</button>
                </div>
              </div>
            </div> */}

            {/* About Me */}
            <div className="profile-form-row single-col">
              <div className="profile-input-group">
                <label className="profile-label">About Me</label>
                {isEditing ? (
                  <textarea
                    className="profile-textarea"
                    value={userFormData.about_self || ""}
                    onChange={(e) => handleUserInputChange("about_self", e.target.value)}
                    placeholder="Tell us about yourself..."
                    rows={3}
                  />
                ) : (
                  <div className="profile-display-value about-me">
                    {userFormData.about_self || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Row 4: Address Line 1 */}
            <div className="profile-form-row single-col">
              <div className="profile-input-group">
                <label className="profile-label">Address Line 1</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.address_line_1 || ""}
                    onChange={(e) => handleUserInputChange("address_line_1", e.target.value)}
                    placeholder="Enter address line 1"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.address_line_1 || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Row 5: Address Line 2 */}
            <div className="profile-form-row single-col">
              <div className="profile-input-group">
                <label className="profile-label">Address Line 2</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.address_line_2 || ""}
                    onChange={(e) => handleUserInputChange("address_line_2", e.target.value)}
                    placeholder="Enter address line 2"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.address_line_2 || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Row 6: Country, State, City, Postal Code */}
            <div className="profile-form-row four-col-address">
              <div className="profile-input-group">
                <label className="profile-label">Country</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.country || ""}
                    onChange={(e) => handleUserInputChange("country", e.target.value)}
                    placeholder="Enter country"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.country || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group">
                <label className="profile-label">State</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.state || ""}
                    onChange={(e) => handleUserInputChange("state", e.target.value)}
                    placeholder="Enter state"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.state || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group city-small">
                <label className="profile-label">City</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.city || ""}
                    onChange={(e) => handleUserInputChange("city", e.target.value)}
                    placeholder="Enter city"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.city || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group postal-small">
                <label className="profile-label">Postal Code</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={userFormData.postal_code || ""}
                    onChange={(e) => handleUserInputChange("postal_code", e.target.value)}
                    placeholder="Code"
                  />
                ) : (
                  <div className="profile-display-value">
                    {userFormData.postal_code || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Social Media Section */}
            <div className="profile-social-section">
              <div className="profile-social-header">
                <label className="profile-label">Social Media</label>
                {isEditing && (
                  <button className="profile-add-btn" onClick={() => addSocialMedia(true)}>
                    <PlusIcon />
                    Add
                  </button>
                )}
              </div>
              
              <div className="profile-social-list">
                {(userFormData.social_media_data || []).map((social: any, index: number) => (
                  <div key={index} className="profile-social-row">
                    {social.isExisting || !isEditing ? (
                      <div className="profile-social-platform-display">
                        {social.platform_name || ""}
                      </div>
                    ) : (
                      <select
                        className="profile-select social-select"
                        value={social.platform_name || ""}
                        onChange={(e) =>
                          handleSocialMediaChange(index, "platform_name", e.target.value, true)
                        }
                      >
                        <option value="">Platform</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Twitter">Twitter</option>
                        <option value="Facebook">Facebook</option>
                        <option value="YouTube">YouTube</option>
                        <option value="YouTube">Other</option>
                      </select>
                    )}
                    {isEditing ? (
                      <input
                        type="text"
                        placeholder="Enter URL here"
                        className="profile-input social-input"
                        value={social.profile_url || ""}
                        onChange={(e) =>
                          handleSocialMediaChange(index, "profile_url", e.target.value, true)
                        }
                      />
                    ) : (
                      <div className="profile-display-value social-url">
                        {social.profile_url || ""}
                      </div>
                    )}
                    {!social.isExisting && isEditing && (
                      <button
                        onClick={() => removeSocialMedia(index, true)}
                        className="profile-remove-btn"
                      >
                        <XIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="profile-actions">
                <button className="profile-cancel-btn" onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  className="profile-save-btn"
                  onClick={updateUserProfile}
                  disabled={updating}
                >
                  {updating ? "Saving..." : "Save Changes"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Company Profile */}
      {activeTab === "company" && shouldShowCompanySection() && (
        <div className="profile-content-wrapper">
          {/* Left Sidebar - Company Card */}
          <div className="profile-sidebar-card">
            <div className="profile-avatar-section">
              <div className="profile-company-icon">
                <BuildingIcon />
              </div>
            </div>

            <div className="profile-user-info">
              <h2 className="profile-user-name">
                {companyProfile?.company_name || "Company Name"}
              </h2>
              <p className="profile-user-company">{companyProfile?.company_type}</p>
              
              <div className="profile-contact-info">
                <div className="profile-contact-item">
                  <MailIcon />
                  <span>{companyProfile?.admin_email}</span>
                </div>
              </div>

              {/* Profile Completion */}
              <div className="profile-completion-section">
                <div className="profile-completion-circle">
                  <svg className="profile-completion-svg" viewBox="0 0 100 100">
                    <defs>
                      <linearGradient id="completionGradientCompany" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#34d399" />
                      </linearGradient>
                    </defs>
                    <circle
                      className="profile-completion-bg"
                      cx="50"
                      cy="50"
                      r="42"
                    />
                    <circle
                      className="profile-completion-fill"
                      cx="50"
                      cy="50"
                      r="42"
                      stroke="url(#completionGradientCompany)"
                      style={{
                        strokeDasharray: `${getProfileCompletion(companyProfile) * 2.64} 264`,
                      }}
                    />
                  </svg>
                  <div className="profile-completion-text">
                    <span className="profile-completion-number">{getProfileCompletion(companyProfile)}%</span>
                  </div>
                </div>
                <p className="profile-completion-label">Profile Complete</p>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="profile-form-card">
            <div className="profile-form-header">
              <h3 className="profile-section-title">Company Information</h3>
              {!isEditing && (
                <button className="profile-edit-btn" onClick={handleEditClick}>
                  <EditIcon />
                  Edit
                </button>
              )}
            </div>

            {/* Row 1: Company Name, Company Type */}
            <div className="profile-form-row two-col">
              <div className="profile-input-group">
                <label className="profile-label">Company Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.company_name || ""}
                    onChange={(e) => handleCompanyInputChange("company_name", e.target.value)}
                    placeholder="Enter company name"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.company_name || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group">
                <label className="profile-label">Company Type</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.company_type || ""}
                    onChange={(e) => handleCompanyInputChange("company_type", e.target.value)}
                    placeholder="Enter company type"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.company_type || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: Website, GST Number */}
            <div className="profile-form-row two-col">
              <div className="profile-input-group">
                <label className="profile-label">Website</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.company_website || ""}
                    onChange={(e) => handleCompanyInputChange("company_website", e.target.value)}
                    placeholder="Enter website URL"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.company_website || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group">
                <label className="profile-label">GST Number</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.gst_number || ""}
                    onChange={(e) => handleCompanyInputChange("gst_number", e.target.value)}
                    placeholder="Enter GST number"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.gst_number || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Row 3: Number of People */}
            <div className="profile-form-row two-col">
              <div className="profile-input-group">
                <label className="profile-label">Number of People</label>
                {isEditing ? (
                  <input
                    type="number"
                    className="profile-input"
                    value={companyFormData.number_of_people || ""}
                    onChange={(e) => handleCompanyInputChange("number_of_people", e.target.value)}
                    placeholder="Enter number"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.number_of_people || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Address Section */}
            <h3 className="profile-section-title" style={{ marginTop: '2rem' }}>Company Address</h3>

            {/* Row: Address Lines */}
            <div className="profile-form-row single-col">
              <div className="profile-input-group">
                <label className="profile-label">Address Line 1</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.address_line_1 || ""}
                    onChange={(e) => handleCompanyInputChange("address_line_1", e.target.value)}
                    placeholder="Enter address line 1"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.address_line_1 || ""}
                  </div>
                )}
              </div>
            </div>

            <div className="profile-form-row two-col">
              <div className="profile-input-group">
                <label className="profile-label">Address Line 2</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.address_line_2 || ""}
                    onChange={(e) => handleCompanyInputChange("address_line_2", e.target.value)}
                    placeholder="Enter address line 2"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.address_line_2 || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group">
                <label className="profile-label">Address Line 3</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.address_line_3 || ""}
                    onChange={(e) => handleCompanyInputChange("address_line_3", e.target.value)}
                    placeholder="Enter address line 3"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.address_line_3 || ""}
                  </div>
                )}
              </div>
            </div>

            <div className="profile-form-row three-col">
              <div className="profile-input-group">
                <label className="profile-label">City</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.city || ""}
                    onChange={(e) => handleCompanyInputChange("city", e.target.value)}
                    placeholder="Enter city"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.city || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group">
                <label className="profile-label">State</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.state || ""}
                    onChange={(e) => handleCompanyInputChange("state", e.target.value)}
                    placeholder="Enter state"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.state || ""}
                  </div>
                )}
              </div>
              <div className="profile-input-group">
                <label className="profile-label">Country</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.country || ""}
                    onChange={(e) => handleCompanyInputChange("country", e.target.value)}
                    placeholder="Enter country"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.country || ""}
                  </div>
                )}
              </div>
            </div>

            <div className="profile-form-row two-col">
              <div className="profile-input-group postal">
                <label className="profile-label">Postal Code</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="profile-input"
                    value={companyFormData.postal_code || ""}
                    onChange={(e) => handleCompanyInputChange("postal_code", e.target.value)}
                    placeholder="Enter code"
                  />
                ) : (
                  <div className="profile-display-value">
                    {companyFormData.postal_code || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Social Media Section */}
            <div className="profile-social-section">
              <div className="profile-social-header">
                <label className="profile-label">Social Media</label>
                {isEditing && (
                  <button className="profile-add-btn" onClick={() => addSocialMedia(false)}>
                    <PlusIcon />
                    Add
                  </button>
                )}
              </div>
              
              <div className="profile-social-list">
                {(companyFormData.social_media_data || []).map((social: any, index: number) => (
                  <div key={index} className="profile-social-row">
                    {social.isExisting || !isEditing ? (
                      <div className="profile-social-platform-display">
                        {social.platform_name || ""}
                      </div>
                    ) : (
                      <select
                        className="profile-select social-select"
                        value={social.platform_name || ""}
                        onChange={(e) =>
                          handleSocialMediaChange(index, "platform_name", e.target.value, false)
                        }
                      >
                        <option value="">Platform</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Twitter">Twitter</option>
                        <option value="Facebook">Facebook</option>
                        <option value="YouTube">YouTube</option>
                      </select>
                    )}
                    {isEditing ? (
                      <input
                        type="text"
                        placeholder="Enter URL here"
                        className="profile-input social-input"
                        value={social.profile_url || ""}
                        onChange={(e) =>
                          handleSocialMediaChange(index, "profile_url", e.target.value, false)
                        }
                      />
                    ) : (
                      <div className="profile-display-value social-url">
                        {social.profile_url || ""}
                      </div>
                    )}
                    {!social.isExisting && isEditing && (
                      <button
                        onClick={() => removeSocialMedia(index, false)}
                        className="profile-remove-btn"
                      >
                        <XIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Admin Details */}
            <h3 className="profile-section-title" style={{ marginTop: '2rem' }}>Admin Details</h3>
            <div className="profile-form-row two-col">
              <div className="profile-input-group">
                <label className="profile-label">Admin Status</label>
                <input
                  type="text"
                  className="profile-input readonly"
                  value={companyFormData.admin_status || ""}
                  readOnly
                />
              </div>
              <div className="profile-input-group">
                <label className="profile-label">Admin Email</label>
                <input
                  type="email"
                  className="profile-input readonly"
                  value={companyFormData.admin_email || ""}
                  readOnly
                />
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="profile-actions">
                <button className="profile-cancel-btn" onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  className="profile-save-btn"
                  onClick={updateCompanyProfile}
                  disabled={updating}
                >
                  {updating ? "Saving..." : "Save Changes"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;